type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface BaseMetadata {
  [key: string]: string | number | boolean | null | undefined;
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  metadata: LogMetadata;
  data?: Record<string, unknown>;
}

interface LogMetadata extends BaseMetadata {
  component: string;
}

const skipLogging = import.meta.env['VITE_HIDE_DEBUG_LOG'] === 'true';

class Logger {
  private static instance: Logger;

  private constructor() {
    // Private constructor for singleton pattern
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private shouldLog(level: LogLevel): boolean {
    // Determine environment based on hostname for browser environments
    const isProduction =
      window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
    if (isProduction || skipLogging) {
      return level === 'error' || level === 'warn';
    }
    return true;
  }

  private formatLogEntry(
    level: LogLevel,
    metadata: LogMetadata,
    message: string,
    data?: Record<string, unknown>,
  ): LogEntry {
    return {
      level,
      message,
      metadata: {
        ...metadata,
        timestamp: new Date().toISOString(),
      },
      data: data || {},
      timestamp: new Date().toISOString(),
    };
  }

  debug(metadata: LogMetadata, message: string, data?: Record<string, unknown>): void {
    // Determine environment based on hostname for browser environments
    const isDevelopment =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isDevelopment && !skipLogging) {
      const logEntry = this.formatLogEntry('debug', metadata, message, data);
      // eslint-disable-next-line no-console
      console.debug(
        `[${logEntry.timestamp}][${logEntry.level}][${logEntry.metadata.component}] ${message}`,
        {
          metadata: logEntry.metadata,
          data,
        },
      );
    }
  }

  info(metadata: LogMetadata, message: string, data?: Record<string, unknown>): void {
    if (this.shouldLog('info')) {
      const logEntry = this.formatLogEntry('info', metadata, message, data);
      // eslint-disable-next-line no-console
      console.info(
        `[${logEntry.timestamp}][${logEntry.level}][${logEntry.metadata.component}] ${message}`,
        {
          metadata: logEntry.metadata,
          data,
        },
      );
    }
  }

  warn(metadata: LogMetadata, message: string, data?: Record<string, unknown>): void {
    if (this.shouldLog('warn')) {
      const logEntry = this.formatLogEntry('warn', metadata, message, data);
      // eslint-disable-next-line no-console
      console.warn(
        `[${logEntry.timestamp}][${logEntry.level}][${logEntry.metadata.component}] ${message}`,
        {
          metadata: logEntry.metadata,
          data,
        },
      );
    }
  }

  error(
    metadata: LogMetadata,
    message: string,
    error?: Error,
    data?: Record<string, unknown>,
  ): void {
    if (this.shouldLog('error')) {
      const logEntry = this.formatLogEntry('error', metadata, message, data);
      // eslint-disable-next-line no-console
      console.error(
        `[${logEntry.timestamp}][${logEntry.level}][${logEntry.metadata.component}] ${message}`,
        {
          metadata: logEntry.metadata,
          error,
          data,
        },
      );

      // Determine environment based on hostname for browser environments
      const isProduction =
        window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
      if (isProduction) {
        // errorTrackingService.capture(error, { metadata: logEntry });
      }
    }
  }
}

export const logger = Logger.getInstance();
